import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext.js";
import api from "../api/api.js";
import MainLayout from "../sidebar/Sidebar.js";
import {
  Search,
  Plus,
  Save,
  Trash2,
  X,
  ChevronDown,
  Mail,
  Phone,
  Building,
  MapPin,
  Loader2,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ContactActionsDropdown } from "../components/ContactActionsDropdown.js";
import { useSearchInvalidation, SEARCH_QUERY_KEYS } from "../hooks/useSearchInvalidation.js";

// Custom hook for debounced search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function ContactsWithAdvancedFiltering() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateCategory } = useSearchInvalidation();
  const [contactTypes, setContactTypes] = useState([]);
  const [location, setLocation] = useState("Contacts");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [isFiltering, setIsFiltering] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState(null); // Added deletingContactId state to track which contact is being deleted
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_type: "Client",
    title: "",
    email: "",
    phone_number: "",
    company: "",
    notes: "",
    address: "",
  });
  const [editingContact, setEditingContact] = useState(null);
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [contactToDeleteId, setContactToDeleteId] = useState(null);
  const [showEditContactModal, setShowEditContactModal] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    data: contacts = [],
    isLoading: contactsLoading,
    error: contactsError,
  } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.contacts(user?.uid),
    queryFn: async () => {
      const response = await api.get("/contacts/");
      return response.data.sort(
        (a, b) =>
          new Date(b.created_at || b.id) - new Date(a.created_at || a.id)
      );
    },
    staleTime: 1 * 60 * 1000, // Reduced to 1 minute for faster updates
    cacheTime: 5 * 60 * 1000, // Reduced to 5 minutes
  });

  const {
    data: contactTypesData = [],
    isLoading: typesLoading,
    error: typesError,
  } = useQuery({
    queryKey: ["contactTypes", user],
    queryFn: async () => {
      const response = await api.get("/contacts/types/list");
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });

  useEffect(() => {
    if (contactTypesData) {
      setContactTypes(contactTypesData);
    }
  }, [contactTypesData]);

  const loading = contactsLoading || typesLoading;
  const errorText = contactsError?.message || typesError?.message || "";

  const createContactMutation = useMutation({
    mutationFn: async (contactData) => {
      const response = await api.post("/contacts/", contactData);
      return response.data;
    },
    onSuccess: (newContact) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.contacts(user?.uid), (oldContacts) => {
        return [newContact, ...(oldContacts || [])];
      });
      // Invalidate to ensure global search index is updated
      invalidateCategory("contacts");
      setShowCreateContactModal(false);
      setFormData({
        contact_name: "",
        contact_type: "Client",
        title: "",
        email: "",
        phone_number: "",
        company: "",
        notes: "",
        address: "",
      });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId) => {
      await api.delete(`/contacts/${contactId}`);
      return contactId;
    },
    onSuccess: (deletedContactId) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.contacts(user?.uid), (oldContacts) =>
        oldContacts.filter((contact) => contact.id !== deletedContactId)
      );
      // Invalidate to ensure global search index is updated
      invalidateCategory("contacts");
      setShowDeleteConfirmModal(false);
      setContactToDeleteId(null);
      setDeletingContactId(null);
    },
    onError: (error) => {
      setDeletingContactId(null);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: async ({ contactId, contactData }) => {
      const response = await api.put(`/contacts/${contactId}`, contactData);
      return response.data;
    },
    onSuccess: (updatedContact) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.contacts(user?.uid), (oldContacts) =>
        (oldContacts || []).map((contact) =>
          contact.id === updatedContact.id ? updatedContact : contact
        )
      );
      // Invalidate to ensure global search index is updated
      invalidateCategory("contacts");
      setEditingContact(null);
      setShowEditContactModal(false);
      setFormData({
        contact_name: "",
        contact_type: "Client",
        title: "",
        email: "",
        phone_number: "",
        company: "",
        notes: "",
        address: "",
      });
    },
  });

  const filteredContacts = useMemo(() => {
    if (!contacts.length) return [];

    // Show loading state briefly when filtering
    if (isFiltering) return [];

    return contacts.filter((contact) => {
      const matchesType =
        typeFilter === "All" || contact.contact_type === typeFilter;
      const matchesSearch =
        contact.contact_name
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase()) ||
        (contact.company &&
          contact.company
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())) ||
        (contact.email &&
          contact.email
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())) ||
        (contact.phone_number &&
          contact.phone_number
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()));
      return matchesType && matchesSearch;
    });
  }, [contacts, typeFilter, debouncedSearchQuery, isFiltering]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!formData.contact_name.trim()) {
      return;
    }

    const contactData = {
      contact_name: formData.contact_name.trim(),
      contact_type: formData.contact_type,
      title: formData.title.trim() || null,
      email: formData.email.trim() || null,
      phone_number: formData.phone_number.trim() || null,
      company: formData.company.trim() || null,
      notes: formData.notes.trim() || null,
      address: formData.address.trim() || null,
    };

    createContactMutation.mutate(contactData);
  };

  const handleDeleteContact = (contactId) => {
    setContactToDeleteId(contactId);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteContact = useCallback(async () => {
    if (!contactToDeleteId) return;

    setDeletingContactId(contactToDeleteId);

    try {
      await deleteContactMutation.mutateAsync(contactToDeleteId);
    } catch (error) {
      console.error("Failed to delete contact:", error);
    }
  }, [contactToDeleteId, deleteContactMutation]);

  const cancelDeleteContact = () => {
    setShowDeleteConfirmModal(false);
    setContactToDeleteId(null);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setFormData({
      contact_name: contact.contact_name,
      contact_type: contact.contact_type,
      title: contact.title || "",
      email: contact.email || "",
      phone_number: contact.phone_number || "",
      company: contact.company || "",
      notes: contact.notes || "",
      address: contact.address || "",
    });
    setShowEditContactModal(true); // Set the modal state
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!editingContact) return;
    if (!formData.contact_name.trim()) {
      return;
    }

    const contactData = {
      contact_name: formData.contact_name.trim(),
      contact_type: formData.contact_type,
      title: formData.title.trim() || null,
      email: formData.email.trim() || null,
      phone_number: formData.phone_number.trim() || null,
      company: formData.company.trim() || null,
      notes: formData.notes.trim() || null,
      address: formData.address.trim() || null,
    };

    updateContactMutation.mutate({
      contactId: editingContact.id,
      contactData,
    });
  };

  const handleCloseEditModal = () => {
    setEditingContact(null);
    setFormData({
      contact_name: "",
      contact_type: "Client",
      title: "",
      email: "",
      phone_number: "",
      company: "",
      notes: "",
      address: "",
    });
    setShowEditContactModal(false); // Clear the modal state
  };

  const handleCloseCreateModal = () => {
    setShowCreateContactModal(false);
    setFormData({
      contact_name: "",
      contact_type: "Client",
      title: "",
      email: "",
      phone_number: "",
      company: "",
      notes: "",
      address: "",
    });
  };

  const handleClearFields = () => {
    setFormData({
      contact_name: "", // Clear contact name too for a full reset
      contact_type: "Client",
      title: "",
      email: "",
      phone_number: "",
      company: "",
      notes: "",
      address: "",
    });
  };

  const getContactTypeColor = (type) => {
    const colors = {
      Client: "bg-blue-100 text-blue-800",
      Contractor: "bg-green-100 text-green-800",
      Supplier: "bg-purple-100 text-purple-800",
      Vendor: "bg-orange-100 text-orange-800",
      Inspector: "bg-red-100 text-red-800",
      Architect: "bg-indigo-100 text-indigo-800",
      Engineer: "bg-teal-100 text-teal-800",
      Subcontractor: "bg-yellow-100 text-yellow-800",
      Other: "bg-gray-100 text-gray-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  function getInitials(name = "") {
    const words = name.trim().split(/\s+/);

    if (words.length === 1) {
      // One word → first 2 letters
      return words[0].slice(0, 2).toUpperCase();
    }

    if (words.length === 2) {
      // Two words → first letter of each
      return (words[0][0] + words[1][0]).toUpperCase();
    }

    // More than two words → first of first + first of last
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }

  const isAnyOverlayActive =
    showCreateContactModal ||
    editingContact ||
    showDeleteConfirmModal ||
    showEditContactModal;

  const isCreating = createContactMutation.isPending;
  const isUpdating = updateContactMutation.isPending;
  const isDeleting = deleteContactMutation.isPending;

  const handleTypeFilterChange = useCallback((newType) => {
    setIsFiltering(true);
    setTypeFilter(newType);

    // Brief loading animation
    setTimeout(() => {
      setIsFiltering(false);
    }, 200);
  }, []);

  // Handle search with loading state
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchQuery(value);

      if (value !== debouncedSearchQuery) {
        setIsFiltering(true);
        // The debounced value will trigger the filter update
        setTimeout(() => {
          setIsFiltering(false);
        }, 350);
      }
    },
    [debouncedSearchQuery]
  );

  const handleContactClick = (contact) => {
    // Handle contact click logic here
  };

  return (
    <>
      <div
        className={`relative transition duration-300 ${
          isAnyOverlayActive ? "blur-sm pointer-events-none select-none" : ""
        }`}
      >
        <MainLayout location={location} setLocation={setLocation}>
          <div className="bg-gray-50 min-h-full py-4 px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
                  <p className="text-gray-600 mt-1">
                    {filteredContacts.length} Contacts
                  </p>
                </div>
              </div>
              <div className="mb-2 flex flex-row sm:flex-row items-center justify-between gap-6 py-4 px-0">
                <div className="w-full sm:hidden relative">
                  <select
                    value={typeFilter}
                    onChange={(e) => handleTypeFilterChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 pr-8 text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  >
                    <option value="" disabled>
                      Select type...
                    </option>
                    {["All", ...contactTypes].map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
                <div className="hidden sm:flex flex-wrap gap-3 justify-start">
                  {["All", ...contactTypes].map((type) => (
                    <button
                      key={type}
                      onClick={() => handleTypeFilterChange(type)}
                      disabled={isFiltering}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                        typeFilter === type
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                      } ${isFiltering ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {isFiltering && typeFilter === type && (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      )}
                      {type}
                    </button>
                  ))}
                </div>
                <div className="relative flex items-center w-full sm:w-96">
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="bg-gray-50 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-2 border border-gray-200 w-full pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                  />
                  <Search className="absolute left-3 text-gray-400 h-5 w-5" />
                  {isFiltering && searchQuery && (
                    <Loader2 className="absolute right-3 text-gray-400 h-4 w-4 animate-spin" />
                  )}
                </div>
              </div>

              {errorText && (
                <div
                  className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8 text-center font-medium"
                  role="alert"
                >
                  {errorText}
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center min-h-screen">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {isFiltering && (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-3 text-gray-500">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-lg font-medium">
                      Filtering contacts...
                    </span>
                  </div>
                </div>
              )}

              {!loading && !isFiltering && filteredContacts.length === 0 && (
                <div className="text-gray-500 text-center py-12 text-xl font-medium">
                  {searchQuery || typeFilter !== "All"
                    ? "No contacts match your filters."
                    : "No contacts found."}
                </div>
              )}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${typeFilter}-${debouncedSearchQuery}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                  >
                    {!searchQuery && (
                      <motion.div
                        key="add-new-contact-card"
                        layoutId="add-new-contact"
                        className="bg-white rounded-xl shadow p-5 flex flex-col items-center justify-center cursor-pointer hover:shadow-md transition-all duration-200 aspect-square"
                        onClick={() => setShowCreateContactModal(true)}
                      >
                        <Plus className="h-12 w-12 text-blue-600 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          Add New Contact
                        </h3>
                        <p className="text-sm text-gray-600 text-center mt-1">
                          Click to create a new contact entry.
                        </p>
                      </motion.div>
                    )}

                    {filteredContacts.map((contact) => {
                      const isDeleting = deletingContactId === contact.id;

                      return (
                        <motion.div
                          key={contact.id}
                          layoutId={`contact-${contact.id}`}
                          className={`bg-white rounded-xl shadow p-5 flex flex-col aspect-square hover:shadow-lg transition-all duration-200 relative cursor-pointer ${
                            isDeleting ? "pointer-events-none" : ""
                          }`}
                          onClick={() => handleContactClick(contact)}
                        >
                          {isDeleting && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="absolute inset-0 bg-white bg-opacity-90 rounded-xl flex items-center justify-center z-10"
                            >
                              <div className="flex flex-col items-center">
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{
                                    duration: 1,
                                    repeat: Number.POSITIVE_INFINITY,
                                    ease: "linear",
                                  }}
                                >
                                  <Trash2 className="h-8 w-8 text-red-500 mb-2" />
                                </motion.div>
                                <p className="text-sm font-medium text-red-600">
                                  Deleting contact...
                                </p>
                              </div>
                            </motion.div>
                          )}

                          <div className="flex justify-between items-start mb-2">
                            <div className="pr-8 flex-1">
                              <h3 className="text-sm font-semibold text-gray-900 leading-tight mb-1">
                                {contact.contact_name}
                              </h3>
                              {contact.title && (
                                <p className="text-xs text-gray-600 line-clamp-2">
                                  {contact.title}
                                </p>
                              )}
                            </div>
                            <div
                              className="flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ContactActionsDropdown
                                contact={contact}
                                onEdit={handleEditContact}
                                onDelete={handleDeleteContact}
                              />
                            </div>
                          </div>

                          <div className="flex items-center mt-2">
                            <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-blue-600 leading-none">
                              {getInitials(contact.contact_name)}
                            </div>
                            <div className="ml-2 flex-1 min-w-0">
                              <span
                                className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getContactTypeColor(
                                  contact.contact_type
                                )}`}
                              >
                                {contact.contact_type}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 mt-3 flex-grow">
                            {contact.company && (
                              <div className="p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center text-xs text-gray-500 mb-1">
                                  <Building className="h-3 w-3 mr-1" />
                                  Company
                                </div>
                                <div className="text-xs font-medium text-gray-900 truncate">
                                  {contact.company}
                                </div>
                              </div>
                            )}
                            {contact.email && (
                              <div className="p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center text-xs text-gray-500 mb-1">
                                  <Mail className="h-3 w-3 mr-1" />
                                  Email
                                </div>
                                <div className="text-xs font-medium text-blue-600 truncate">
                                  {contact.email}
                                </div>
                              </div>
                            )}
                            {contact.phone_number && (
                              <div className="p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center text-xs text-gray-500 mb-1">
                                  <Phone className="h-3 w-3 mr-1" />
                                  Phone
                                </div>
                                <div className="text-xs font-medium text-blue-600 truncate">
                                  {contact.phone_number}
                                </div>
                              </div>
                            )}
                            {contact.address && (
                              <div className="p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center text-xs text-gray-500 mb-1">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  Address
                                </div>
                                <div className="text-xs font-medium text-gray-900 truncate">
                                  {contact.address}
                                </div>
                              </div>
                            )}
                          </div>

                          {contact.notes && (
                            <div className="mt-2">
                              <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                Click Card to View Notes
                              </span>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>
              </div>
          </div>
        </MainLayout>
      </div>

      <AnimatePresence>
        {showCreateContactModal && (
          <motion.div
            key="createContactModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={handleCloseCreateModal}
          >
            <motion.div
              key="createContactModalContent"
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={handleCloseCreateModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                aria-label="Close create contact modal"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-8 text-center">
                Create New Contact
              </h2>
              <form onSubmit={handleCreateContact} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="contact_name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="Enter contact name..."
                      required
                    />
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="contact_type"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="contact_type"
                        name="contact_type"
                        value={formData.contact_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 h-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out bg-white appearance-none"
                        required
                      >
                        {contactTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Title
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="e.g., Project Manager"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone_number"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone_number"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="Company name"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                    placeholder="Full address"
                  />
                </div>
                <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                    placeholder="Additional notes about this contact..."
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={handleClearFields}
                    className="px-6 py-3 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors duration-200"
                  >
                    Clear all fields
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCreating ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-5 w-5" />
                        Create Contact
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditContactModal && editingContact && (
          <motion.div
            key="editContactModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={handleCloseEditModal}
          >
            <motion.div
              key="editContactModalContent"
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={handleCloseEditModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                aria-label="Close edit modal"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-8 text-center">
                Edit Contact
              </h2>
              <form onSubmit={handleUpdateContact} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="edit_contact_name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="edit_contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      required
                    />
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="edit_contact_type"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="edit_contact_type"
                        name="contact_type"
                        value={formData.contact_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 h-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out bg-white appearance-none"
                        required
                      >
                        {contactTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="edit_title"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Title
                    </label>
                    <input
                      type="text"
                      id="edit_title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="e.g., Project Manager"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="edit_email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="edit_email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="edit_phone_number"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="edit_phone_number"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="edit_company"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      id="edit_company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                      placeholder="Company name"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="edit_address"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Address
                  </label>
                  <input
                    type="text"
                    id="edit_address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                    placeholder="Full address"
                  />
                </div>
                <div>
                  <label
                    htmlFor="edit_notes"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Notes
                  </label>
                  <textarea
                    id="edit_notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                    placeholder="Additional notes about this contact..."
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="px-6 py-3 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUpdating ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteConfirmModal && (
          <motion.div
            key="deleteModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={cancelDeleteContact}
          >
            <motion.div
              key="deleteModalContent"
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg relative"
            >
              <button
                onClick={cancelDeleteContact}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                aria-label="Close delete modal"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Confirm Deletion
              </h2>
              <p className="text-gray-700 mb-8">
                Are you sure you want to delete this contact? This action cannot
                be undone.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  onClick={cancelDeleteContact}
                  className="px-6 py-3 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteContact}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-5 w-5" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
